import i18next from 'i18next';
import { toast } from 'sonner';

import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useTenantStore } from '@/store/tenant.store.js';

const SUCCESS_MESSAGE_DEFAULTS = Object.freeze({
    recovered: 'Delivery recovery completed.',
    exported: 'Recipients exported.',
    created: 'Notification policy created.',
    saved: 'Draft saved.',
    draftDeleted: 'Draft deleted.',
    simulated: 'Simulation completed.',
    testDelivered: 'Test notification delivered.',
    published: 'Notification policy published.',
    rolledBack: 'Notification policy rolled back.',
    modeChanged: 'Notification policy mode changed.',
    enabled: 'Notification policy enabled.',
    disabled: 'Notification policy disabled.',
});

const FAILURE_MESSAGE_DEFAULTS = Object.freeze({
    failed: 'Notification policy operation failed.',
    testDeliveryFailed: 'Test notification could not be delivered.',
    recoveryFailed: 'Delivery recovery failed.',
});

export class NotificationRoutingIntegration {
    constructor(service) { this.service = service; }

    async #read(operation, fallback = null) {
        if (!useTenantStore.getState().selectedTenantId) return { success: false, data: fallback, error: 'MISSING_TENANT' };
        try { return await operation(); } catch (error) {
            return { success: false, data: fallback, error: getIntegrationErrorMessage(error), details: error.details || [] };
        }
    }

    async #write(operation, messageKey, failureKey = 'failed') {
        try {
            const result = await operation();
            toast.success(
                i18next.t(`notificationPolicy:messages.${messageKey}`, {
                    defaultValue: SUCCESS_MESSAGE_DEFAULTS[messageKey] || 'Operation completed.',
                }),
            );
            return result;
        } catch (error) {
            toast.error(
                getIntegrationErrorMessage(error)
                || i18next.t(`notificationPolicy:messages.${failureKey}`, {
                    defaultValue: FAILURE_MESSAGE_DEFAULTS[failureKey]
                        || FAILURE_MESSAGE_DEFAULTS.failed,
                }),
            );
            return { success: false, error: getIntegrationErrorMessage(error), details: error.details || [] };
        }
    }

    catalog() { return this.#read(() => this.service.catalog(), []); }
    listManagementEvents(params = {}) {
        return this.#read(() => this.service.listManagementEvents(params), { data: [], total: 0, page: 1, limit: 25 });
    }
    managementEventWorkspace(eventName) { return this.#read(() => this.service.managementEventWorkspace(eventName)); }
    listRecordSources(eventName, params = {}) {
        return this.#read(() => this.service.listRecordSources(eventName, params), {
            available: false, reasonCode: 'RECORD_SOURCE_UNAVAILABLE', data: [],
            page: params.page || 1, limit: params.limit || 10, total: 0,
            hasMore: false, truncated: false, scanned: 0,
        });
    }
    listPolicies(params = {}) { return this.#read(() => this.service.listPolicies(params), { data: [], total: 0 }); }
    getPolicy(id) { return this.#read(() => this.service.getPolicy(id)); }
    effective(params = {}) { return this.#read(() => this.service.effective(params), []); }
    listDispatches(params = {}) { return this.#read(() => this.service.listDispatches(params), { data: [], total: 0 }); }
    getDispatch(id) { return this.#read(() => this.service.getDispatch(id)); }
    explainDispatch(id) { return this.#read(() => this.service.explainDispatch(id)); }
    recoverDelivery(dispatchId, intentId, data) {
        return this.#write(
            () => this.service.recoverDelivery(dispatchId, intentId, data),
            'recovered',
            'recoveryFailed',
        );
    }
    exportRecipients(id) { return this.#write(() => this.service.exportRecipients(id), 'exported'); }
    createPolicy(data) { return this.#write(() => this.service.createPolicy(data), 'created'); }
    updateDraft(id, data) { return this.#write(() => this.service.updateDraft(id, data), 'saved'); }
    deleteDraft(id, data) { return this.#write(() => this.service.deleteDraft(id, data), 'draftDeleted'); }
    validateDraft(id, data) { return this.#read(() => this.service.validateDraft(id, data)); }
    audienceInspection(id, data) { return this.#read(() => this.service.audienceInspection(id, data)); }
    simulate(id, data) { return this.#write(() => this.service.simulate(id, data), 'simulated'); }
    getVerification(id, verificationId) { return this.#read(() => this.service.getVerification(id, verificationId)); }
    verificationRecipients(id, verificationId) {
        return this.#read(() => this.service.verificationRecipients(id, verificationId));
    }
    previewDeliver(id, data) { return this.#write(() => this.service.previewDeliver(id, data), 'testDelivered', 'testDeliveryFailed'); }
    livePilot(id, data) { return this.#write(() => this.service.livePilot(id, data), 'testDelivered', 'testDeliveryFailed'); }
    previewResult(id) { return this.#read(() => this.service.previewResult(id)); }
    livePilotResult(id) { return this.#read(() => this.service.livePilotResult(id)); }
    testDeliver(id, data) { return this.#write(() => this.service.testDeliver(id, data), 'testDelivered', 'testDeliveryFailed'); }
    testResult(id) { return this.#read(() => this.service.testResult(id)); }
    publish(id, data) { return this.#write(() => this.service.publish(id, data), 'published'); }
    rollback(id, data) { return this.#write(() => this.service.rollback(id, data), 'rolledBack'); }
    changeMode(id, data) { return this.#write(() => this.service.changeMode(id, data), 'modeChanged'); }
    enable(id, data) { return this.#write(() => this.service.enable(id, data), 'enabled'); }
    disable(id, data) { return this.#write(() => this.service.disable(id, data), 'disabled'); }
}
